import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import { DynamicFilterConfig, DynamicFilterValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import {
  APPROVAL_ENTITY_TYPES,
  ApprovalEntityType,
  ApprovalPolicy,
  ApprovalPolicySearchCommand,
} from '../../../models/approval-policy/approval-policy';
import { ApprovalPolicyRoles } from '../../../models/roles/roles.group';
import { ResponseTranslation } from '../../../models/translation';
import { ApprovalPolicyProxyService } from '../../../proxies/approval-policy.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';

/** isActive filter values: the dynamic filter only carries numbers or strings. */
const STATUS_ACTIVE = 1;
const STATUS_INACTIVE = 0;

@Component({
  selector: 'app-approval-policies',
  templateUrl: './approval-policies.component.html',
})
export class ApprovalPoliciesComponent {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly approvalPolicyProxyService = inject(ApprovalPolicyProxyService);

  readonly canEdit = AuthService.hasRole(ApprovalPolicyRoles.ApprovalPolicyAdmin);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'approval-policy-filters',
    config: this.filterConfig,
    defaults: { isActive: STATUS_ACTIVE },
  });
  policies = signal<ApprovalPolicy[]>([]);

  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  private currentSearchCriteria: ApprovalPolicySearchCommand = { isActive: true };

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        if (this.translateService.currentLang) this.initializeFilterConfig();
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private initializeFilterConfig(): void {
    const entityOptions: FilterOption[] = APPROVAL_ENTITY_TYPES.map((t) => ({
      label: this.translate(t.labelKey, t.fallback),
      value: t.value,
    }));
    const statusOptions: FilterOption[] = [
      { label: this.translate('ApprovalPolicies_Status_Active', 'Active'), value: STATUS_ACTIVE },
      { label: this.translate('ApprovalPolicies_Status_Inactive', 'Inactive'), value: STATUS_INACTIVE },
    ];

    this.filterConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'name',
          label: this.translate('Name', 'Name'),
          placeholder: this.translate('ApprovalPolicies_Search_Name', 'Search policy'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'isActive',
          label: this.translate('ApprovalPolicies_Status', 'Status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'appliesTo',
          label: this.translate('ApprovalPolicies_AppliesTo', 'Applies to'),
          options: entityOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'defaultFor',
          label: this.translate('ApprovalPolicies_DefaultFor', 'Default for'),
          options: entityOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const status = filters['isActive'];
    const name = filters['name'];
    this.currentSearchCriteria = {
      name: typeof name === 'string' && name.trim() ? name.trim() : null,
      isActive: _.isNil(status) || status === '' ? null : Number(status) === STATUS_ACTIVE,
      appliesTo: this.toCodes(filters['appliesTo']),
      defaultFor: this.toCodes(filters['defaultFor']),
    };
    this.currentPage.set(1);
    this.search();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  onSort(event: SortEvent): void {
    const order = event.order ?? -1;
    if (event.field === this.sortField() && order === this.sortOrder()) return;
    this.sortField.set(event.field);
    this.sortOrder.set(order);
    this.currentPage.set(1);
    this.search();
  }

  redirectToDetails(id: string | null): void {
    this.router.navigate(['/approval-policy-details'], { queryParams: { id: id ?? 'new' } });
  }

  // ---- Row display

  localized(value: ResponseTranslation): string {
    return this.localeHelper.getLocalizedValue(value);
  }

  composition(policy: ApprovalPolicy): string {
    const required = policy.approvers.filter((a) => a.isRequired).length;
    const optional = policy.approvers.length - required;
    const parts: string[] = [];
    if (required > 0) {
      const word = this.translate(required === 1 ? 'ApprovalPolicies_Required_One' : 'ApprovalPolicies_Required_Many', 'required');
      parts.push(`${required} ${word}`);
    }
    if (optional > 0) {
      const word = this.translate(optional === 1 ? 'ApprovalPolicies_Optional_One' : 'ApprovalPolicies_Optional_Many', 'optional');
      parts.push(`${optional} ${word}`);
    }
    return parts.join(' · ');
  }

  isSequential(policy: ApprovalPolicy): boolean {
    return policy.approvers.some((a) => !_.isNil(a.order));
  }

  /** The lowest threshold and who it brings in, e.g. "USD 5,000.00 (CFO)". Null when every approver always applies. */
  threshold(policy: ApprovalPolicy): string | null {
    const amounts = policy.approvers.flatMap((a) => (_.isNil(a.fromAmountUsd) ? [] : [a.fromAmountUsd]));
    if (amounts.length === 0) return null;
    const lowest = Math.min(...amounts);
    const who = policy.approvers.filter((a) => a.fromAmountUsd === lowest).map((a) => a.value);
    return `USD ${lowest.toLocaleString('en-US', { minimumFractionDigits: 2 })} (${who.join(', ')})`;
  }

  entityLabel(code: ApprovalEntityType): string {
    const option = APPROVAL_ENTITY_TYPES.find((t) => t.value === code);
    return option ? this.translate(option.labelKey, option.fallback) : String(code);
  }

  entityLabels(codes: ApprovalEntityType[]): string {
    return codes.map((c) => this.entityLabel(c)).join(', ');
  }

  private toCodes(value: unknown): ApprovalEntityType[] | null {
    if (!Array.isArray(value) || value.length === 0) return null;
    return value.map((v) => Number(_.isObject(v) ? (v as FilterOption).value : v));
  }

  /** The name is a Translation: sort on the language the user is reading. */
  private apiSortField(): string | undefined {
    const field = this.sortField();
    return field === 'name' ? `name.${this.translateService.currentLang === 'es' ? 'es' : 'en'}` : field;
  }

  private search(): void {
    this.startLoading();
    this.approvalPolicyProxyService
      .search(this.currentSearchCriteria, this.currentPage(), this.pageSize(), this.apiSortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.policies.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }
}
